/**
 * Org structure — the model every view derives from.
 *
 * Built from two sources, merged by unit id:
 *
 *   GET /api/v1/units/list   the tree itself, and the authoritative unit set.
 *                            The same call the Organisation Hierarchy screen
 *                            makes, so both show the same units under the same
 *                            access rules. It carries the embedded `posts`,
 *                            which is where sanctioned strength actually lives,
 *                            and unlike the org-structure endpoint it does not
 *                            drop inactive units.
 *
 *   GET /api/v1/org-structure  personnel headcount per unit, which only that
 *                            endpoint computes. Enrichment, not structure — if
 *                            it fails the chart still draws, without the
 *                            people figures.
 *
 * Only the DIRECT headcount is taken from the second source; the rollup is done
 * here, over this tree. Taking its rollup instead would count a subtree this
 * tree does not have (it omits inactive units), so the totals would not add up
 * to what is on screen.
 */
import type React from 'react';
import type { Unit } from '../../types';

/** A row as `GET /api/v1/units/list` returns it. */
export type UnitRow = Unit;

/** Direct personnel per unit id, from the org-structure endpoint. */
export type PersonnelIndex = Record<string, number>;

export interface OrgHead {
  name: string;
  badge?: string;
  rank?: string;
  phone?: string;
  email?: string;
}

export interface OrgNode {
  id: string;
  name: string;
  /** Unit code, or an em dash when the master has none. */
  code: string;
  /** Unit type name, upper-cased for the card's second line. */
  typeLabel: string;
  parentId: string | null;
  /** Depth below this node's own root — drives colour and indentation. */
  lvl: number;
  kids: string[];
  /** Ancestors, root first. */
  path: string[];
  head: OrgHead | null;
  /**
   * Why `head` is what it is.
   *
   *   filled   somebody holds the unit's head post
   *   vacant   there IS a head post and nobody is in it — a real vacancy
   *   unknown  the unit records no head post at all, so who is in charge is
   *            simply not on file. Painting this red would report a vacancy
   *            that nobody has actually observed.
   */
  headState: 'filled' | 'vacant' | 'unknown';
  /**
   * The name of the post the officer holds — and nothing else.
   *
   * This used to fall back to the unit type and then to the literal word
   * "Unit", which meant it could never be empty, so every place that printed
   * it printed something. On a force with no `posts` recorded anywhere that
   * put the unit type under the officer's name, where it reads as the
   * officer's rank: a card for a police station said "Police Station" twice,
   * once correctly as the type and once as a job nobody holds.
   *
   * Null when the unit records no head post. A caller that wants the unit type
   * should ask for `typeLabel`, which is what it actually is.
   */
  headRank: string | null;

  /* ── strength, from the unit's embedded posts ── */
  /** Sanctioned posts on this unit alone. */
  posts: number;
  /** Of those, the ones with someone assigned. */
  filled: number;
  /** Sanctioned posts across this unit and everything under it. */
  rPosts: number;
  /** Filled posts across this unit and everything under it. */
  rFilled: number;

  /* ── headcount, merged in from the org-structure endpoint ── */
  /** Personnel posted to this unit alone. 0 when that source had nothing. */
  direct: number;
  /** Personnel across this unit and everything under it. */
  people: number;

  /** Units at every level below this one. */
  subUnits: number;
  districtName?: string;
  departmentName?: string;
  isActive: boolean;
  raw: UnitRow;
}

export interface OrgModel {
  byId: Record<string, OrgNode>;
  /** Every id, parents before children. */
  ids: string[];
  roots: string[];
  descendants: (id: string) => string[];
  /** Units whose head post exists and stands empty. */
  vacantCount: number;
  /** Units that record no head post at all — unknown, not vacant. */
  unknownHeadCount: number;
  /** False when nothing in scope carries post data, so strength figures mean nothing. */
  hasPostData: boolean;
  /** Personnel across every root. */
  totalPeople: number;
  /** Sanctioned and filled posts across every root. */
  totalPosts: number;
  totalFilled: number;
  /** True when the headcount source answered, so the People figures mean something. */
  hasHeadcount: boolean;
  /** Deepest level present, so the depth filter only offers real levels. */
  maxLevel: number;
}

const EMPTY: string[] = [];

/** Posts that count: not deleted, not deactivated. */
const livePosts = (u: UnitRow) => (u.posts || []).filter((p) => !p.isDelete && p.isActive !== false);

/**
 * Who is in charge.
 *
 * The unit's own `posts` are the source of truth: the post flagged `isUnitHead`
 * and who is assigned to it. `responsibleUser` is the fallback, and it is only a
 * fallback because the list endpoint populates it without a name — just a login
 * id and an email, which is the least useful thing to put on a card.
 */
const headOf = (u: UnitRow): { head: OrgHead | null; state: OrgNode['headState']; rank: string | null } => {
  const post = livePosts(u).find((p) => p.isUnitHead);
  if (post?.assignedUser) {
    return {
      head: { name: post.assignedUser.name, badge: post.assignedUser.employeeId || undefined },
      state: 'filled',
      rank: post.postName || null,
    };
  }
  const responsible = u as { responsibleUser?: { userId?: string; email?: string } };
  const fallback = u.responsibleUserName || responsible.responsibleUser?.userId || responsible.responsibleUser?.email;
  if (fallback) return { head: { name: fallback }, state: 'filled', rank: post?.postName || null };
  // Only a head post that exists and stands empty is a vacancy. No head post on
  // record means we were never told — a different thing, and not a red card.
  return { head: null, state: post ? 'vacant' : 'unknown', rank: post?.postName || null };
};

const parentOf = (u: UnitRow): string | null => {
  if (u.parentUnitId) return String(u.parentUnitId);
  if (u.parentUnit && typeof u.parentUnit === 'object' && u.parentUnit._id) return String(u.parentUnit._id);
  return null;
};

/** Pull the direct headcount out of the org-structure tree, ignoring its own rollups. */
interface PersonnelNode {
  unitId: string;
  personnelCount?: number;
  children?: PersonnelNode[];
}

export const indexPersonnel = (roots: readonly PersonnelNode[] | undefined): PersonnelIndex => {
  const out: PersonnelIndex = {};
  const walk = (n: PersonnelNode) => {
    if (!n?.unitId || out[n.unitId] !== undefined) return;
    out[n.unitId] = n.personnelCount ?? 0;
    n.children?.forEach(walk);
  };
  roots?.forEach(walk);
  return out;
};

export const buildOrgModel = (rows: UnitRow[], personnel: PersonnelIndex = {}): OrgModel => {
  const byId: Record<string, OrgNode> = {};
  const ids: string[] = [];

  for (const u of rows || []) {
    const id = u?._id ? String(u._id) : '';
    // A duplicate id would give one unit two places in the tree; first wins.
    if (!id || byId[id]) continue;
    const live = livePosts(u);
    const { head, state, rank } = headOf(u);
    byId[id] = {
      id,
      name: u.name || 'Unnamed unit',
      code: u.unitCode || u.policeReferenceId || '—',
      typeLabel: (typeof u.unitType === 'object' ? u.unitType?.name : u.unitType) || 'Unit',
      parentId: parentOf(u),
      lvl: 0,
      kids: [],
      path: [],
      head,
      headState: state,
      headRank: rank,
      posts: live.length,
      filled: live.filter((p) => !!p.assignedUser).length,
      rPosts: 0,
      rFilled: 0,
      direct: personnel[id] ?? 0,
      people: 0,
      subUnits: 0,
      districtName: (typeof u.district === 'object' ? u.district?.name : undefined) || undefined,
      departmentName: (typeof u.department === 'object' ? u.department?.name : undefined) || undefined,
      isActive: u.isActive !== false,
      raw: u,
    };
    ids.push(id);
  }

  // A parent outside the returned set (hierarchy scoping can cut one off) makes
  // this node a root, rather than an orphan that never renders.
  const roots: string[] = [];
  for (const id of ids) {
    const p = byId[id].parentId;
    if (p && byId[p]) byId[p].kids.push(id);
    else {
      byId[id].parentId = null;
      roots.push(id);
    }
  }

  /*
   * Depth and ancestor path, walked from the roots.
   *
   * Every unit has at most one parent, so the only way a cycle can exist is a
   * chain that closes on one of its own ancestors — and that ancestor is always
   * already visited when we reach the closing edge. Dropping the edge HERE, out
   * of `kids`, is what matters: guarding this traversal alone would leave the
   * back-edge in the data for the rollups, the descendant cache and the layout
   * to walk round forever.
   */
  const seen = new Set<string>();
  const walk = (id: string, path: string[], depth: number) => {
    seen.add(id);
    byId[id].path = path;
    byId[id].lvl = depth;
    byId[id].kids = byId[id].kids.filter((k) => !seen.has(k));
    const next = [...path, id];
    byId[id].kids.forEach((k) => walk(k, next, depth + 1));
  };
  roots.forEach((r) => walk(r, [], 0));
  // A cycle with no way in from any root leaves its members unvisited. Cut the
  // first one loose as a root; walking it breaks the loop for the rest.
  for (const id of ids) {
    if (!seen.has(id)) {
      byId[id].parentId = null;
      roots.push(id);
      walk(id, [], 0);
    }
  }

  // Rollups, deepest first, so a parent only ever reads finished children.
  const order = [...ids].sort((a, b) => byId[b].lvl - byId[a].lvl);
  for (const id of order) {
    const n = byId[id];
    n.rPosts = n.posts;
    n.rFilled = n.filled;
    n.people = n.direct;
    n.subUnits = 0;
    for (const k of n.kids) {
      const c = byId[k];
      n.rPosts += c.rPosts;
      n.rFilled += c.rFilled;
      n.people += c.people;
      n.subUnits += 1 + c.subUnits;
    }
  }

  const cache: Record<string, string[]> = {};
  const descendants = (id: string): string[] => {
    if (cache[id]) return cache[id];
    if (!byId[id]) return EMPTY;
    const out: string[] = [];
    const w = (x: string) => byId[x].kids.forEach((k) => { out.push(k); w(k); });
    w(id);
    cache[id] = out;
    return out;
  };

  // Parents before children, so the outline can render straight down the array.
  const ordered: string[] = [];
  const emit = (id: string) => { ordered.push(id); byId[id].kids.forEach(emit); };
  roots.forEach(emit);

  return {
    byId,
    ids: ordered,
    roots,
    descendants,
    vacantCount: ordered.filter((id) => byId[id].headState === 'vacant').length,
    unknownHeadCount: ordered.filter((id) => byId[id].headState === 'unknown').length,
    hasPostData: ordered.some((id) => byId[id].posts > 0),
    totalPeople: roots.reduce((s, id) => s + byId[id].people, 0),
    totalPosts: roots.reduce((s, id) => s + byId[id].rPosts, 0),
    totalFilled: roots.reduce((s, id) => s + byId[id].rFilled, 0),
    hasHeadcount: Object.keys(personnel).length > 0,
    maxLevel: ordered.reduce((m, id) => Math.max(m, byId[id].lvl), 0),
  };
};

/* ── formatting helpers shared by every view ── */

export const nf = (n: number) => n.toLocaleString('en-IN');

/**
 * Share of a command's sanctioned posts standing empty, as a percentage.
 *
 * `null` when the unit has no posts on record at all — which is not the same as
 * "fully staffed", and must not be drawn as 0%.
 */
export const vacancyPct = (n: OrgNode): number | null =>
  n.rPosts > 0 ? Math.round(((n.rPosts - n.rFilled) / n.rPosts) * 100) : null;

/** Semantic colour for a vacancy rate: fine, worth watching, or a problem. */
export const vacancyTone = (pct: number | null): string =>
  pct === null ? 'var(--org-ink-3)'
    : pct >= 20 ? 'var(--org-danger)'
    : pct >= 12 ? 'var(--org-warn)'
    : 'var(--org-ok)';

/** Level colour, capped at the deepest token the palette defines. */
export const levelColour = (n: OrgNode) => `var(--org-l${Math.min(5, n.lvl)})`;

/**
 * The colour a unit's strip, tick or block takes: ALWAYS its level.
 *
 * Depth and whether anyone is in charge are two independent facts, and folding
 * them into one colour channel loses both. It did exactly that: when no unit
 * carried post data every card came out the same grey, and the level ramp — the
 * only thing the block tier can show — disappeared entirely.
 *
 * Head state gets its own channel instead: red type on the officer line, and a
 * red edge on the card, which survives down to the block tier where there is no
 * type left to colour.
 */
export const headColour = (n: OrgNode) => levelColour(n);

/**
 * The ink to write ON a unit's level colour — its initials, chiefly.
 *
 * One blanket white was wrong in both directions: it cleared 15.9:1 on the deep
 * end of the ramp and 1.67:1 on the pale end, where the initials simply were not
 * there. Each level names the ink it was measured against instead; the worst
 * pair on either theme now clears 4.88:1.
 */
export const headInk = (n: OrgNode) => `var(--org-l${Math.min(5, n.lvl)}-on)`;

/** Background and ink together, for the one element that needs both. */
export const avatarStyle = (n: OrgNode) => ({
  background: headColour(n),
  ['--org-on' as string]: headInk(n),
}) as React.CSSProperties;

/**
 * The headcount figure, or an em dash when the source that computes it did not
 * answer. Rendering 0 there would report an empty unit, which is a claim we are
 * in no position to make.
 */
export const peopleLabel = (model: OrgModel, n: OrgNode) => (model.hasHeadcount ? nf(n.people) : '—');

/** What to write where the officer's name would go. */
export const headLabel = (n: OrgNode) =>
  n.head ? n.head.name : n.headState === 'vacant' ? 'No officer in charge' : 'Officer not recorded';

/** Two initials, taken from the end of the name so "K. Naga Raju" reads NR. */
export const initials = (name: string) =>
  name
    .replace(/\./g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

/** Which field answered the search, and how strong the answer was. */
export type MatchField = 'name' | 'code' | 'officer' | 'type' | 'district';

/**
 * The one place a unit is tested against the search box.
 *
 * The result list and the dimming on the chart both go through here, so a card
 * can never be highlighted by one and unreachable by the other — which is
 * exactly the bug you get from writing the predicate twice.
 *
 * Ranked so the likeliest intent comes first: a name that starts with what was
 * typed, then a name that contains it, then the code, then the officer, then
 * the softer fields.
 */
export const matchField = (n: OrgNode, q: string): { field: MatchField; score: number } | null => {
  if (!q) return null;
  const name = n.name.toLowerCase();
  if (name.startsWith(q)) return { field: 'name', score: 100 };
  if (name.includes(q)) return { field: 'name', score: 80 };
  if (n.code.toLowerCase().includes(q)) return { field: 'code', score: 60 };
  if ((n.head?.name || '').toLowerCase().includes(q)) return { field: 'officer', score: 40 };
  if (n.typeLabel.toLowerCase().includes(q)) return { field: 'type', score: 20 };
  if ((n.districtName || '').toLowerCase().includes(q)) return { field: 'district', score: 10 };
  return null;
};

/** Does this unit answer the search box and the vacancy filter? */
export const matches = (n: OrgNode, q: string, vacantOnly: boolean) => {
  if (vacantOnly && n.headState !== 'vacant') return false;
  if (!q) return true;
  return matchField(n, q) !== null;
};

export interface SearchHit {
  node: OrgNode;
  /** Which field answered, for the row's second line. */
  via: MatchField;
  score: number;
}

/**
 * Every unit that answers the query, best first.
 *
 * This walks the whole model, not the laid-out chart — a match inside a folded
 * branch is exactly the one you cannot find by eye, so it must be in the list.
 * Ties break shallowest-first: someone typing "guntur" more often wants the
 * district than one of its stations.
 */
export const searchUnits = (model: OrgModel, q: string, vacantOnly: boolean): SearchHit[] => {
  if (!q) return [];
  const out: SearchHit[] = [];
  for (const id of model.ids) {
    const n = model.byId[id];
    if (vacantOnly && n.headState !== 'vacant') continue;
    const m = matchField(n, q);
    if (!m) continue;
    out.push({ node: n, via: m.field, score: m.score - n.lvl });
  }
  return out.sort((a, b) => b.score - a.score || a.node.name.localeCompare(b.node.name));
};

/**
 * A unit's line of command: every ancestor above it, itself, and everything
 * under it — the map from the top of the tree, through this unit, to the bottom.
 *
 * This is what the chart draws once a unit is picked. Blurring the rest was not
 * enough on a real force: a blurred card still occupies its slot, so the chosen
 * line stayed spread across the same 12,000px plane it was lost in. Taking the
 * rest out of the layout is what actually collapses the chart down to the line.
 */
export const lineageOf = (model: OrgModel, id: string): Set<string> => {
  const u = model.byId[id];
  if (!u) return new Set<string>();
  return new Set<string>([...u.path, id, ...model.descendants(id)]);
};

/**
 * What to unfold when a unit is picked: its ancestors, itself, and ONE level of
 * what reports to it.
 *
 * One level, not the whole subtree. "Open the entire flow" under a station is
 * nothing; under a district it is twenty-odd units, and under a state head
 * office it is every unit in the force — dropping all of them on the canvas
 * loses the flow rather than showing it. Worse, it is what stops the chart
 * stacking: peers only list under their parent when none of THEM branch, so
 * unfolding the grandchildren is exactly what forces twenty stations to fan
 * out 6,000px wide and fit at 18%, where a card is a blank coloured block.
 *
 * Held to one level, the same district is a short stacked list that fits on
 * screen whole, and the `+N` markers say what is underneath.
 */
export const revealFlow = (
  model: OrgModel,
  id: string,
  collapsed: Set<string>,
  levels = 1
): { collapsed: Set<string>; revealed: number; hidden: number } => {
  const u = model.byId[id];
  if (!u) return { collapsed, revealed: 0, hidden: 0 };

  const next = new Set(collapsed);
  u.path.forEach((a) => next.delete(a));

  let revealed = 0;
  let frontier = [id];
  for (let l = 0; l < levels; l += 1) {
    frontier.forEach((x) => next.delete(x));
    const kids = frontier.flatMap((x) => model.byId[x].kids);
    revealed += kids.length;
    frontier = kids;
  }
  // Whatever is left on the frontier stays folded. That is what keeps the level
  // below it off the canvas — and what makes the stack possible.
  frontier.forEach((x) => {
    if (model.byId[x].kids.length) next.add(x);
  });

  return { collapsed: next, revealed, hidden: model.descendants(id).length - revealed };
};
